package com.arena_web.ui.components.widgets

import androidx.compose.runtime.Composable
import com.arena_web.ui.components.cards.BookingField
import com.arena_web.ui.components.cards.PaymentStatus
import com.arena_web.ui.localization.LocalAppStrings
import com.arena_web.ui.theme.*
import com.arena_web.ui.utils.*
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.graphics.Color
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.silk.components.text.SpanText

// =============================================================================
// Shared badge container
// =============================================================================

private const val BADGE_GRADIENT =
    "linear-gradient(7deg, rgba(0, 59, 174, 0.07) 27%, rgba(3, 87, 230, 0.21) 68%)"

@Composable
private fun BadgePill(
    modifier: Modifier = Modifier,
    content: @Composable () -> Unit
) {
    Box(
        modifier
            .sBorderRadius(22)
            .styleModifier {
                property("border", "0.1px solid rgba(255, 255, 255, 0.4)")
                property("background", BADGE_GRADIENT)
                property("padding", "${s(2)} ${s(8)}")
                property("display", "flex")
                property("align-items", "center")
                property("justify-content", "center")
            }
    ) {
        content()
    }
}

// =============================================================================
// Modal/Field badge — 78x22 pill with colored dot + field label
// =============================================================================

/** Dot colors per Figma ModalField component set (node 5109:11473) */
private fun fieldDotColor(field: BookingField): Color = when (field) {
    BookingField.Field01 -> AppColors.Primary              // #0061FF
    BookingField.Field02 -> SportColors.Tennis.primary      // #7BFA6F
    BookingField.Field03 -> Color.hex("FFA438")            // #FFA438
}

@Composable
fun ModalFieldBadge(
    field: BookingField,
    modifier: Modifier = Modifier
) {
    val t = LocalAppStrings.current
    BadgePill(modifier) {
        Row(
            Modifier.styleModifier { property("gap", s(6)) },
            verticalAlignment = Alignment.CenterVertically
        ) {
            // Field color dot (6x7, circle offset 1px from top per Figma SVG)
            Box(
                Modifier
                    .sWidth(6)
                    .sHeight(7)
                    .styleModifier {
                        property("display", "flex")
                        property("align-items", "flex-end")
                    }
            ) {
                Box(
                    Modifier
                        .sSize(6)
                        .sBorderRadius(3)
                        .backgroundColor(fieldDotColor(field))
                )
            }
            SpanText(
                t.widgetFieldLabel(field.number),
                modifier = Modifier.bodyLRegular().sLineHeight(18)
                    .color(AppColors.Text.Secondary)
                    .styleModifier { property("white-space", "nowrap") }
            )
        }
    }
}

// =============================================================================
// BookingStatus badge — auto-width pill with status label
// =============================================================================

@Composable
fun ModalBookingStatusBadge(
    status: String,
    modifier: Modifier = Modifier
) {
    BadgePill(modifier) {
        SpanText(
            status,
            modifier = Modifier.bodyLRegular().sLineHeight(18)
                .color(AppColors.Text.Secondary)
                .styleModifier {
                    property("white-space", "nowrap")
                    property("text-align", "center")
                }
        )
    }
}

// =============================================================================
// PaymentStatus badge — auto-width pill with colored status label
// =============================================================================

@Composable
fun ModalPaymentStatusBadge(
    paymentStatus: PaymentStatus,
    modifier: Modifier = Modifier
) {
    BadgePill(modifier) {
        SpanText(
            paymentStatus.label,
            modifier = Modifier.bodyLRegular().sLineHeight(18)
                .color(paymentStatus.labelColor)
                .styleModifier { property("white-space", "nowrap") }
        )
    }
}
